import { PriorityIcon } from "../../ui/Priority";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  assignTask,
  listActionDecisions,
  listTasks,
  resolveActionDecision,
  resumeActionDecision,
  updateTask,
  type ActionDecision,
} from "../../api/hub/work";
import type { Person, Task } from "../../api/hub/types";
import { useWorkDirectory, priorityLabels } from "../tasks/useTasks";
import { ownerLabel, safeReference } from "../tasks/TaskMeta";
import { QuickTaskInput } from "../tasks/QuickTaskInput";
import { taskDate } from "../tasks/time";
import { nextFocusDay } from "./date";
import "../tasks/tasks.css";
import "./decisions.css";
import { DecisionEvidence } from "./DecisionEvidence";
const kindLabels: Record<string, string> = {
  task_acceptance: "完成验收",
  assignment: "接受指派",
  duplicate: "疑似重复",
  proposal: "变更提议",
  triage_confirmation: "确认任务",
  agent_request: "代理提问",
  approval: "飞书审批",
};
const taskFor = (d: ActionDecision, tasks: Task[]) =>
  d.entityType === "task" ? tasks.find((t) => t.id === d.entityId) : undefined;
export function sortDecisions(items: ActionDecision[], tasks: Task[]) {
  return [...items].sort((a, b) => {
    const ta = taskFor(a, tasks),
      tb = taskFor(b, tasks);
    const blocked = (d: ActionDecision, t?: Task) =>
      Number(
        d.entityType === "task" &&
          (!!d.blocking ||
            tasks.some(
              (other) =>
                !["done", "canceled"].includes(other.status) &&
                other.blockedBy?.includes(t?.id ?? ""),
            )),
      );
    const stale = (t?: Task) =>
      Number(
        t?.issueFlags?.some((f) =>
          /stuck|stale|stalled|卡住/.test(
            typeof f === "string" ? f : (f.kind ?? f.code ?? f.message ?? ""),
          ),
        ),
      );
    return (
      Number(!a.dueDate) - Number(!b.dueDate) ||
      blocked(b, tb) - blocked(a, ta) ||
      (ta?.priority || a.priority || 5) - (tb?.priority || b.priority || 5) ||
      stale(tb) - stale(ta) ||
      Number(!!tb?.okrId) - Number(!!ta?.okrId) ||
      (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999") ||
      (a.createdAt ?? 0) - (b.createdAt ?? 0)
    );
  });
}
function DecisionCard({
  decision: d,
  tasks,
  people,
  meId,
}: {
  decision: ActionDecision;
  tasks: Task[];
  people: Person[];
  meId?: string;
}) {
  const qc = useQueryClient(),
    task = taskFor(d, tasks);
  const [reassign, setReassign] = useState("");
  const mayDecide = !!meId && d.deciderIds.includes(meId);
  const fixed = d.execution?.request;
  const mayRecover =
    mayDecide &&
    d.execution?.actorId === meId &&
    d.execution.recoverable &&
    !!fixed &&
    fixed.optionId === d.execution.optionId &&
    ["prepared", "applied", "executing", "needs_review"].includes(
      d.execution.state,
    );
  const refresh = () => qc.invalidateQueries({ queryKey: ["hub"] });
  const recovery = useMutation({
    mutationFn: () => resumeActionDecision(d),
    onSettled: refresh,
  });
  const resolve = useMutation({
    mutationFn: (optionId: string) => {
      const [assigneeType, assigneeId] = reassign.split(":");
      const reassignment =
        d.kind === "triage_confirmation" && optionId === "confirm" && assigneeId
          ? { assigneeId, assigneeType: assigneeType as "person" | "agent" }
          : undefined;
      return resolveActionDecision(d.id, optionId, d.version, reassignment);
    },
    onSettled: refresh,
  });
  return (
    <article
      className="decision-row"
      aria-label={`${kindLabels[d.kind] ?? "待决定"}：${d.question}`}
    >
      <div className="work-meta">
        {kindLabels[d.kind] ?? "待确认"}
        {d.submitterName && ` · ${d.submitterName}`}
        {d.dueDate && ` · 截止 ${d.dueDate.slice(0, 10)}`}
        {d.blocking && " · 挡着其他任务"}
      </div>
      <h2>{d.question}</h2>
      <p>
        <b>建议</b> {d.recommendation || "请结合依据选择。"}
      </p>
      <DecisionEvidence
        value={d.evidence || "尚未附依据"}
        responsibilityId={d.responsibilityId}
        sourceUrl={d.sourceUrl}
        task={task}
      />
      <p className="work-meta">
        不处理：
        {d.impact ??
          d.consequence ??
          (d.blocking
            ? "关联工作会继续等待。"
            : d.kind === "task_acceptance"
              ? "任务保持待验收。"
              : "此项保持待决定，后续处理继续等待。")}
      </p>
      {d.kind === "triage_confirmation" && d.status === "open" && mayDecide && (
        <label className="work-toolbar">
          确认时改派
          <select
            aria-label={`确认时改派：${d.question}`}
            value={reassign}
            disabled={resolve.isPending}
            onChange={(e) => setReassign(e.target.value)}
          >
            <option value="">保持当前负责人</option>
            {people.flatMap((p) => [
              <option key={p.id} value={`person:${p.id}`}>
                {p.name}
              </option>,
              <option key={`ai:${p.id}`} value={`agent:${p.id}`}>
                {p.name}的 AI
              </option>,
            ])}
          </select>
        </label>
      )}
      {d.status !== "open" && (
        <section aria-label="决定执行状态">
          <p role="status">
            {d.status === "needs_review"
              ? "决定已固定，执行结果需要核对。核对前不能改选或重新执行。"
              : "决定已固定，正在处理或等待恢复。"}
          </p>
          {d.execution?.optionId && (
            <p>
              固定选项：
              {d.options.find((option) => option.id === d.execution?.optionId)
                ?.label ?? d.execution.optionId}
            </p>
          )}
          {fixed?.assigneeId && (
            <p>
              确认时改派：
              {ownerLabel(
                {
                  assigneeId: fixed.assigneeId,
                  assigneeType: fixed.assigneeType ?? "person",
                },
                people,
              )}
            </p>
          )}
          {fixed?.conclusion && <p>固定结论：{fixed.conclusion}</p>}
          {d.execution?.error && <p role="note">{d.execution.error}</p>}
          {d.execution?.logId && (
            <Link
              to={`/decisions?decision=${encodeURIComponent(d.execution.logId)}`}
            >
              核对决策记录
            </Link>
          )}
          {mayRecover && (
            <p className="work-meta">
              {d.execution?.state === "prepared"
                ? "原决定已保存，继续时按原选择处理。"
                : "已有完整业务回执，仅恢复决定记录，不重发业务动作。"}
            </p>
          )}
          {mayRecover && (
            <button
              disabled={recovery.isPending}
              onClick={() => recovery.mutate()}
            >
              按原决定安全恢复
            </button>
          )}
          {d.execution?.actorId && d.execution.actorId !== meId && (
            <p className="work-meta">只能由原决定人恢复。</p>
          )}
        </section>
      )}
      {d.status === "open" && (mayDecide || d.kind === "approval") && (
        <div className="work-toolbar">
          {d.kind === "approval" ? (
            <>
              <Link
                to={`/approvals?approval=${encodeURIComponent(d.entityId ?? "")}`}
              >
                查看审批并人工处理
              </Link>
              {d.sourceUrl && safeReference(d.sourceUrl) && (
                <a href={d.sourceUrl} target="_blank" rel="noreferrer">
                  在飞书拍板
                </a>
              )}
            </>
          ) : (
            d.options.map((o) => (
              <button
                key={o.id}
                disabled={resolve.isPending}
                onClick={() => resolve.mutate(o.id)}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
      {d.status === "open" && !mayDecide && d.kind !== "approval" && (
        <p className="work-meta">当前账号不是这项决定的决定人。</p>
      )}
      {recovery.isError && (
        <p role="alert">
          {recovery.error.message}。原决定已保留，请核对执行状态。
        </p>
      )}
      {resolve.isError && (
        <p role="alert">{resolve.error.message}。请刷新核对最新状态。</p>
      )}
    </article>
  );
}
export function FocusPage() {
  const directory = useWorkDirectory(),
    qc = useQueryClient(),
    [error, setError] = useState("");
  const decisions = useQuery({
      queryKey: ["hub", "action-decisions", "mine"],
      queryFn: listActionDecisions,
    }),
    taskQuery = useQuery({
      queryKey: ["hub", "tasks", "include-triage"],
      queryFn: () => listTasks(true),
    });
  const all = taskQuery.data ?? [],
    me = directory.me.data,
    timeZone = String(directory.settings.data?.timeZone || "Asia/Shanghai"),
    today = taskDate(Date.now(), timeZone);
  const end = new Date(`${today}T12:00:00`);
  end.setDate(end.getDate() + ((7 - end.getDay()) % 7));
  const weekEnd = taskDate(end.getTime(), timeZone);
  const due = all
    .filter(
      (t) =>
        t.assigneeId === me?.id &&
        !["done", "canceled", "triage"].includes(t.status) &&
        !!t.dueDate,
    )
    .sort(
      (a, b) =>
        (a.dueDate ?? "").localeCompare(b.dueDate ?? "") ||
        (a.priority || 5) - (b.priority || 5),
    );
  const action = useMutation({
    mutationFn: async ({ task, type }: { task: Task; type: string }) =>
      type === "agent"
        ? assignTask(task.id, me!.id, "agent", task.version)
        : updateTask(task.id, {
            ...(type === "complete"
              ? { status: "done" as const }
              : { dueDate: nextFocusDay(task.dueDate, timeZone) }),
            expectedUpdatedAt: task.updatedAt,
          }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["hub"] }),
    onError: (e) => setError(e.message),
  });
  const list = (items: Task[]) =>
    items.length ? (
      <ul className="due-list">
        {items.map((t) => (
          <li key={t.id}>
            <div>
              <Link to={`/tasks?task=${t.id}`}>{t.title}</Link>
              <p className="work-meta">
                {ownerLabel(t, directory.people.data ?? [])} ·{" "}
                <PriorityIcon priority={t.priority} label={false} />{" "}
                {priorityLabels[t.priority]} · {taskDate(t.dueDate!, timeZone)}
              </p>
            </div>
            <div className="work-toolbar">
              <button
                disabled={action.isPending}
                onClick={() => action.mutate({ task: t, type: "complete" })}
              >
                完成
              </button>
              <button
                disabled={action.isPending}
                onClick={() => action.mutate({ task: t, type: "delay" })}
              >
                延期一天
              </button>
              {t.assigneeType !== "agent" && (
                <button
                  disabled={action.isPending || !me}
                  onClick={() => action.mutate({ task: t, type: "agent" })}
                >
                  交给我的 AI
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p className="work-empty">没有到期的事</p>
    );
  return (
    <section className="work-page decision-home">
      <header className="work-heading">
        <h1>等我拍板</h1>
        <div className="work-heading-actions">
          <button
            className="work-icon-button"
            aria-label="刷新"
            title="刷新"
            onClick={() => void qc.invalidateQueries({ queryKey: ["hub"] })}
          >
            <RefreshCw aria-hidden="true" />
          </button>
        </div>
      </header>
      {decisions.isPending || directory.me.isPending ? (
        <p role="status">正在加载待决定…</p>
      ) : decisions.isError ? (
        <p role="alert">{decisions.error.message}</p>
      ) : (
        <section aria-label="等我拍板">
          {sortDecisions(
            (decisions.data ?? []).filter(
              (d) =>
                ["open", "resolving", "needs_review"].includes(d.status) &&
                (!d.deciderIds.length || d.deciderIds.includes(me?.id ?? "")),
            ),
            all,
          ).map((d) => (
            <DecisionCard
              key={d.id}
              decision={d}
              tasks={all}
              people={directory.people.data ?? []}
              meId={me?.id}
            />
          ))}
          {!decisions.data?.length && (
            <p className="work-empty">没有待拍板的事</p>
          )}
        </section>
      )}
      <QuickTaskInput />
      {error && <p role="alert">{error}</p>}
      {taskQuery.isError && (
        <p role="alert">到期任务读取失败：{taskQuery.error.message}</p>
      )}
      <section aria-label="今天到期">
        <h2>今天到期</h2>
        {list(due.filter((t) => taskDate(t.dueDate!, timeZone) === today))}
      </section>
      <section aria-label="本周到期">
        <h2>本周到期</h2>
        {list(
          due.filter((t) => {
            const date = taskDate(t.dueDate!, timeZone);
            return date > today && date <= weekEnd;
          }),
        )}
      </section>
    </section>
  );
}
